import React from 'react';
import Chart from 'react-apexcharts';
import { Box } from '@mui/material';

/**
 * Histogram chart with optional overlaid distribution curves and LSL / Avg / USL reference lines.
 *
 * Props:
 *  histData        – array of { x, y, fillColor? } – histogram bin counts; per-point fillColor supported
 *  actualCurve     – array of { x, y } – actual (fitted) distribution curve (omit or [] to hide)
 *  normalCurve     – array of { x, y } – theoretical normal curve (omit or [] to hide)
 *  lsl             – Lower Spec Limit position
 *  usl             – Upper Spec Limit position
 *  avg             – Process average (vertical reference line)
 *  lslLabel        – Annotation label text for LSL line  (default 'LSL')
 *  uslLabel        – Annotation label text for USL line  (default 'USL')
 *  avgLabel        – Annotation label text for Avg line  (default 'Avg')
 *  labelBgColor    – Background color of annotation labels (default 'transparent')
 *  labelTextColor  – Text color of annotation labels     (default '#f44336')
 *  labelFontSize   – Font size of annotation labels      (default '12px')
 *  xMin / xMax     – x-axis range (defaults to lsl-2 / usl+3)
 *  height          – chart height in px                  (default 220)
 */
const HistogramChart = ({
  histData = [],
  actualCurve = [],
  normalCurve = [],
  lsl = 700,
  usl = 720,
  avg = 705.5,
  lslLabel = 'LSL',
  uslLabel = 'USL',
  avgLabel = 'Avg',
  labelBgColor = 'transparent',
  labelTextColor = '#f44336',
  labelFontSize = '12px',
  width = '98%',
  xMin,
  xMax,
  height = 220,
}) => {
  const resolvedXMin = xMin ?? lsl - 2;
  const resolvedXMax = xMax ?? usl + 3;

  // Only include curve series when they contain data
  const series = [
    { name: 'Frequency', type: 'bar', data: histData },
    ...(actualCurve.length > 0 ? [{ name: 'Actual', type: 'line', data: actualCurve }] : []),
    ...(normalCurve.length > 0 ? [{ name: 'Normal', type: 'line', data: normalCurve }] : []),
  ];

  const options = {
    chart: {
      type: 'bar',
      background: '#111',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      fontFamily: "'CPF Imm Sook', sans-serif",
    },
    colors: ['#5b9bd5', '#f44336', '#555555'],
    stroke: {
      width: [0, 2.5, 2.5],
      curve: 'straight',
      dashArray: [0, 0, 6],
      lineCap: 'round',
    },
    fill: {
      opacity: [0.85, 1, 1],
    },
    plotOptions: {
      bar: {
        columnWidth: width,
        borderRadius: 0,
      },
    },
    grid: {
      show: true,
      borderColor: '#2a2a2a',
      strokeDashArray: 4,
      padding: { top: 0, bottom: 0, left: 10, right: 10 },
    },
    xaxis: {
      type: 'numeric',
      min: resolvedXMin,
      max: resolvedXMax,
      tickAmount: 5,
      labels: {
        formatter: (v) => Number(v).toFixed(0),
        style: {
          colors: '#888',
          fontFamily: "'CPF Imm Sook', sans-serif",
          fontSize: '12px',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      show: false,
    },
    annotations: {
      xaxis: [
        {
          x: lsl,
          borderColor: '#f44336',
          strokeDashArray: 5,
          borderWidth: 1.5,
          label: {
            text: lslLabel,
            position: 'top',
            offsetY: -2,
            borderColor: 'transparent',
            borderWidth: 0,
            style: {
              color: labelTextColor,
              background: labelBgColor,
              fontFamily: "'CPF Imm Sook', sans-serif",
              fontSize: labelFontSize,
              fontWeight: 'bold',
              padding: { top: 4, bottom: 4, left: 8, right: 8 },
            },
          },
        },
        {
          x: avg,
          borderColor: '#f44336',
          strokeDashArray: 0,
          borderWidth: 2,
          label: {
            text: avgLabel,
            position: 'top',
            offsetY: -2,
            borderColor: 'transparent',
            borderWidth: 0,
            style: {
              color: labelTextColor,
              background: labelBgColor,
              fontFamily: "'CPF Imm Sook', sans-serif",
              fontSize: labelFontSize,
              fontWeight: 'bold',
              padding: { top: 4, bottom: 4, left: 8, right: 8 },
            },
          },
        },
        {
          x: usl,
          borderColor: '#f44336',
          strokeDashArray: 5,
          borderWidth: 1.5,
          label: {
            text: uslLabel,
            position: 'top',
            offsetY: -2,
            borderColor: 'transparent',
            borderWidth: 0,
            style: {
              color: labelTextColor,
              background: labelBgColor,
              fontFamily: "'CPF Imm Sook', sans-serif",
              fontSize: labelFontSize,
              fontWeight: 'bold',
              padding: { top: 4, bottom: 4, left: 8, right: 8 },
            },
          },
        },
      ],
    },
    tooltip: { enabled: false },
    dataLabels: { enabled: false },
    legend: { show: false },
    theme: { mode: 'dark' },
  };

  return (
    <Box sx={{ width: '100%' }}>
      {histData.length === 0 ? (
        <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#444', fontFamily: "'CPF Imm Sook', sans-serif", fontSize: 14 }}>No data</span>
        </Box>
      ) : (
        <Chart
          options={options}
          series={series}
          type="bar"
          height={height}
        />
      )}
    </Box>
  );
};

export default HistogramChart;
